/**
 * RoadGrader — grade-limited height profiles for road edges.
 *
 * Walks each edge polyline at fixed SAMPLE_STEP intervals, samples terrain
 * height, applies Gaussian smoothing, then clamps consecutive height deltas
 * so the slope never exceeds `maxGrade`. The resulting Float32Array is stored
 * on `edge.gradeProfile` for later use by TerrainStamper.
 *
 * Pure math — zero Three.js dependencies.
 */

import type { RoadGraph, RoadEdge } from '../config.js';

export type HeightSampler = (x: number, z: number) => number;

/** Distance between consecutive height samples along the arc-length (meters). */
const SAMPLE_STEP = 2;

/** Gaussian σ in samples. */
const GAUSS_SIGMA = 3;

/** Kernel half-width = ceil(3 * σ). */
const GAUSS_HALF = Math.ceil(3 * GAUSS_SIGMA); // 9

// ---------------------------------------------------------------------------
// Pre-computed Gaussian kernel (normalised)
// ---------------------------------------------------------------------------

/**
 * Build a 1-D Gaussian kernel of half-width `halfWidth` and σ `sigma`.
 * Returns an array of length `2 * halfWidth + 1` that sums to 1.
 */
function buildGaussianKernel(halfWidth: number, sigma: number): Float64Array {
  const len = 2 * halfWidth + 1;
  const k = new Float64Array(len);
  let sum = 0;
  for (let i = 0; i < len; i++) {
    const d = i - halfWidth;
    k[i] = Math.exp(-(d * d) / (2 * sigma * sigma));
    sum += k[i];
  }
  for (let i = 0; i < len; i++) k[i] /= sum;
  return k;
}

const KERNEL = buildGaussianKernel(GAUSS_HALF, GAUSS_SIGMA);

// ---------------------------------------------------------------------------
// RoadGrader
// ---------------------------------------------------------------------------

export class RoadGrader {
  /**
   * Grade all edges in the graph — mutates `edge.gradeProfile` in place.
   */
  static gradeAll(graph: RoadGraph, sampler: HeightSampler, maxGrade: number): void {
    for (const edge of graph.edges) {
      RoadGrader.gradeEdge(edge, sampler, maxGrade);
    }
  }

  /**
   * Grade a single edge — mutates `edge.gradeProfile` in place.
   *
   * Algorithm:
   * 1. Walk the polyline at SAMPLE_STEP intervals, sampling terrain height.
   * 2. Apply 1-D Gaussian smoothing (σ=3 samples, half-width=9).
   * 3. Forward pass: clamp upward/downward slope to maxGrade.
   * 4. Backward pass: clamp in reverse to prevent one-sided bias.
   * 5. Store result as Float32Array on edge.gradeProfile.
   */
  static gradeEdge(edge: RoadEdge, sampler: HeightSampler, maxGrade: number): void {
    // ------------------------------------------------------------------
    // Step 1 — compute sample positions along the arc-length
    // ------------------------------------------------------------------
    const pts = edge.polyline;           // [x0,z0, x1,z1, ...]
    const ptCount = pts.length / 2;

    if (ptCount < 2) {
      // Degenerate: single point — emit one sample.
      const h = sampler(pts[0], pts[1]);
      edge.gradeProfile = new Float32Array([h, h]);
      return;
    }

    // Compute total arc-length.
    let totalLen = 0;
    const segLens = new Float64Array(ptCount - 1);
    for (let i = 0; i < ptCount - 1; i++) {
      const dx = pts[(i + 1) * 2    ] - pts[i * 2    ];
      const dz = pts[(i + 1) * 2 + 1] - pts[i * 2 + 1];
      segLens[i] = Math.sqrt(dx * dx + dz * dz);
      totalLen += segLens[i];
    }

    // Number of intervals and samples.
    // Always include the start (arc=0) and end (arc=totalLen) points.
    let nIntervals = Math.max(1, Math.round(totalLen / SAMPLE_STEP));
    // Correct: for a 50m road the spec says 26 samples (25 intervals + endpoint).
    // Use totalLen / SAMPLE_STEP rounded to nearest integer for intervals.
    nIntervals = Math.round(totalLen / SAMPLE_STEP);
    if (nIntervals < 1) nIntervals = 1;
    const nSamples = nIntervals + 1;

    const rawHeights = new Float64Array(nSamples);

    // Walk along the arc and interpolate positions.
    let segIdx = 0;
    let segAccum = 0; // arc-length consumed in segments [0..segIdx-1]

    for (let si = 0; si < nSamples; si++) {
      const targetArc = si === nSamples - 1
        ? totalLen
        : si * (totalLen / nIntervals);

      // Advance through segments to find which segment contains targetArc.
      while (
        segIdx < ptCount - 2 &&
        segAccum + segLens[segIdx] < targetArc - 1e-9
      ) {
        segAccum += segLens[segIdx];
        segIdx++;
      }

      const segLen = segLens[segIdx];
      let t: number;
      if (segLen < 1e-12) {
        t = 0;
      } else {
        t = (targetArc - segAccum) / segLen;
        t = Math.max(0, Math.min(1, t));
      }

      const ax = pts[segIdx * 2    ];
      const az = pts[segIdx * 2 + 1];
      const bx = pts[(segIdx + 1) * 2    ];
      const bz = pts[(segIdx + 1) * 2 + 1];

      const sx = ax + t * (bx - ax);
      const sz = az + t * (bz - az);

      rawHeights[si] = sampler(sx, sz);
    }

    // ------------------------------------------------------------------
    // Step 2 — Gaussian smoothing (mirror padding at boundaries)
    // ------------------------------------------------------------------
    const smoothed = new Float64Array(nSamples);
    for (let i = 0; i < nSamples; i++) {
      let val = 0;
      for (let ki = 0; ki < KERNEL.length; ki++) {
        const di = ki - GAUSS_HALF;
        let idx = i + di;
        // Mirror-pad at boundaries.
        if (idx < 0) idx = -idx;
        if (idx >= nSamples) idx = 2 * (nSamples - 1) - idx;
        // Clamp any remaining out-of-range after double-reflection.
        idx = Math.max(0, Math.min(nSamples - 1, idx));
        val += KERNEL[ki] * rawHeights[idx];
      }
      smoothed[i] = val;
    }

    // ------------------------------------------------------------------
    // Step 3 — Forward pass: clamp slope i→i+1
    // ------------------------------------------------------------------
    const step = totalLen / nIntervals; // arc-length between samples
    const maxDelta = maxGrade * step;

    for (let i = 0; i < nSamples - 1; i++) {
      const diff = smoothed[i + 1] - smoothed[i];
      if (diff > maxDelta) {
        smoothed[i + 1] = smoothed[i] + maxDelta;
      } else if (diff < -maxDelta) {
        smoothed[i + 1] = smoothed[i] - maxDelta;
      }
    }

    // ------------------------------------------------------------------
    // Step 4 — Backward pass: clamp slope i+1→i
    // ------------------------------------------------------------------
    for (let i = nSamples - 1; i > 0; i--) {
      const diff = smoothed[i] - smoothed[i - 1];
      if (diff > maxDelta) {
        smoothed[i - 1] = smoothed[i] - maxDelta;
      } else if (diff < -maxDelta) {
        smoothed[i - 1] = smoothed[i] + maxDelta;
      }
    }

    // ------------------------------------------------------------------
    // Step 5 — Store as Float32Array
    // ------------------------------------------------------------------
    const profile = new Float32Array(nSamples);
    for (let i = 0; i < nSamples; i++) profile[i] = smoothed[i];
    edge.gradeProfile = profile;
  }
}
